import { Droppable, Draggable } from "react-beautiful-dnd";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import { Content } from "@/types";
import { MenuSection } from "@/types/menus";
import { SECTION_LIST_DROPPABLE } from "../hooks/useCatalogEditor";
import SectionCard from "./SectionCard";

const DroppableComp = Droppable as any;
const DraggableComp = Draggable as any;

interface Props {
  structure: MenuSection[];
  allContent: Content[];
  expandedSections: Record<string, boolean>;
  onToggleExpand: (category: string) => void;
  onAddSection: () => void;
  onCollapseAll: () => void;
  onUpdateSection: (idx: number, patch: Partial<MenuSection>) => void;
  onRemoveSection: (idx: number) => void;
  onRemoveProduct: (sectionIdx: number, productId: string) => void;
  selectedSectionCategory: string | null;
  onSelectSection: (category: string) => void;
}

const SectionsColumn = ({
  structure,
  allContent,
  expandedSections,
  onToggleExpand,
  onAddSection,
  onCollapseAll,
  onUpdateSection,
  onRemoveSection,
  onRemoveProduct,
  selectedSectionCategory,
  onSelectSection,
}: Props) => {
  const areAllCollapsed = structure.every(
    (section) => expandedSections[section.category] === false
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 2, pb: 1.5, height: 45 }}
      >
        <Typography variant="h4">Secciones</Typography>
        <Stack direction="row" spacing={1}>
          {structure.length > 1 && (
            <Button
              size="small"
              onClick={onCollapseAll}
              variant="outlined"
            >
              {areAllCollapsed ? "Expandir Todo" : "Colapsar Todo"}
            </Button>
          )}
          <Button
            size="small"
            onClick={onAddSection}
            variant="contained"
          >
            Crear Sección
          </Button>
        </Stack>
      </Stack>

      <Divider />

      {/* Droppable section list */}
      <DroppableComp droppableId={SECTION_LIST_DROPPABLE} type="SECTION">
        {(provided: import("react-beautiful-dnd").DroppableProvided, snapshot: import("react-beautiful-dnd").DroppableStateSnapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              height: "calc(100vh - 210px)",
              overflowY: "auto",
              p: 1.5,
              bgcolor: snapshot.isDraggingOver ? "action.focus" : "transparent",
              transition: "background-color 0.15s",
            }}
          >
            {structure.length === 0 ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Aún no hay secciones.
                  <br />
                  Haz clic en «Crear Sección» para comenzar.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                {structure.map((section, idx) => (
                  <DraggableComp
                    key={section.category}
                    draggableId={`section-drag-${section.category}`}
                    index={idx}
                  >
                    {(prov: import("react-beautiful-dnd").DraggableProvided) => (
                      <SectionCard
                        innerRef={prov.innerRef}
                        draggableProps={prov.draggableProps}
                        dragHandleProps={prov.dragHandleProps}
                        section={section}
                        sectionIndex={idx}
                        allContent={allContent}
                        expanded={expandedSections[section.category] !== false}
                        onToggleExpand={() => onToggleExpand(section.category)}
                        onUpdateSection={(patch) => onUpdateSection(idx, patch)}
                        onRemoveSection={() => onRemoveSection(idx)}
                        onRemoveProduct={(pid) => onRemoveProduct(idx, pid)}
                        isSelected={selectedSectionCategory === section.category}
                        onSelectSection={() => onSelectSection(section.category)}
                      />
                    )}
                  </DraggableComp>
                ))}
              </Stack>
            )}
            {provided.placeholder as any}
          </Box>
        )}
      </DroppableComp>
    </Paper>
  );
};

export default SectionsColumn;
